/**
 * Main CRM Layout with dual-panel design
 * Left: AI Assistant + Quick Actions
 * Right: Context Panel with current entity details
 * Both panels are collapsible
 */

import React, { useState, useEffect, createContext, useContext } from "react";
import {
  Layout,
  theme,
  Button,
  Tooltip,
  Dropdown,
  Space,
  Avatar,
  message,
} from "antd";
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  LeftOutlined,
  RightOutlined,
  UserOutlined,
  LogoutOutlined,
} from "@ant-design/icons";
import { useLogout, useGetIdentity } from "@refinedev/core";
import { useLocation } from "react-router-dom";
import { ContextBar } from "./ContextBar";
import { AssistantPanel } from "../assistant/AssistantPanel";
import { ContextPanel } from "../context/ContextPanel";
import { CommandPalette } from "../command/CommandPalette";
import { TaskPanel } from "../tasks/TaskPanel";
import { useContextStore } from "@/hooks/useContextStore";

// Context for RelatedPanel state (to be used by Show views)
interface RelatedPanelContextType {
  isRelatedPanelOpen: boolean;
  setRelatedPanelOpen: (open: boolean) => void;
  relatedPanelContent: React.ReactNode | null;
  setRelatedPanelContent: (content: React.ReactNode | null) => void;
}

const RelatedPanelContext = createContext<RelatedPanelContextType>({
  isRelatedPanelOpen: false,
  setRelatedPanelOpen: () => {},
  relatedPanelContent: null,
  setRelatedPanelContent: () => {},
});

export const useRelatedPanel = () => useContext(RelatedPanelContext);

const { Header, Content, Sider } = Layout;

interface CRMLayoutProps {
  children?: React.ReactNode;
}

// LocalStorage keys
const LEFT_COLLAPSED_KEY = "crm_left_collapsed";
const RIGHT_COLLAPSED_KEY = "crm_right_collapsed";

export const CRMLayout: React.FC<CRMLayoutProps> = ({ children }) => {
  const {
    token: { colorBgContainer },
  } = theme.useToken();

  const location = useLocation();
  const { activeContextIds, contexts } = useContextStore();
  const { mutate: logout } = useLogout();
  const { data: user } = useGetIdentity<{ id: string; name: string }>();

  // User menu items
  const userMenuItems = [
    {
      key: "logout",
      icon: <LogoutOutlined />,
      label: "Logout",
      onClick: () => logout(),
    },
  ];

  // Panel collapse state - load from localStorage
  const [leftCollapsed, setLeftCollapsed] = useState(() => {
    const saved = localStorage.getItem(LEFT_COLLAPSED_KEY);
    return saved === "true";
  });
  const [rightCollapsed, setRightCollapsed] = useState(() => {
    const saved = localStorage.getItem(RIGHT_COLLAPSED_KEY);
    return saved === "true";
  });

  // Related panel state
  const [isRelatedPanelOpen, setRelatedPanelOpen] = useState(false);
  const [relatedPanelContent, setRelatedPanelContent] =
    useState<React.ReactNode | null>(null);

  // Detect if we're on a detail/show view (URL pattern: /resource/:id without /edit)
  const isDetailView =
    /^\/[^/]+\/[^/]+$/.test(location.pathname) &&
    !location.pathname.includes("/create") &&
    !location.pathname.includes("/edit");

  // Track if navigation came from entity history (don't collapse in that case)
  const isHistoryNavigationRef = React.useRef(false);

  // Listen for entity history navigation events
  useEffect(() => {
    const handleHistoryNavigate = () => {
      isHistoryNavigationRef.current = true;
    };
    window.addEventListener("entity-history-navigate", handleHistoryNavigate);
    return () =>
      window.removeEventListener(
        "entity-history-navigate",
        handleHistoryNavigate
      );
  }, []);

  // Auto-collapse context panel only when entering detail view from list (not from history navigation)
  useEffect(() => {
    // Skip if this navigation came from entity history
    if (isHistoryNavigationRef.current) {
      isHistoryNavigationRef.current = false;
      return;
    }

    // Only auto-collapse when entering a detail view
    // This happens when clicking from list view, not when navigating history
    if (isDetailView) {
      setRightCollapsed(true);
    }
  }, [location.pathname]);

  // Close related panel when leaving detail view
  useEffect(() => {
    if (!isDetailView) {
      setRelatedPanelOpen(false);
      setRelatedPanelContent(null);
    }
  }, [isDetailView, location.pathname]);

  // Save to localStorage when state changes
  useEffect(() => {
    localStorage.setItem(LEFT_COLLAPSED_KEY, String(leftCollapsed));
  }, [leftCollapsed]);

  useEffect(() => {
    localStorage.setItem(RIGHT_COLLAPSED_KEY, String(rightCollapsed));
  }, [rightCollapsed]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't trigger if user is typing in an input
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement
      ) {
        return;
      }
      if (e.key === "[") {
        setLeftCollapsed((prev) => !prev);
      } else if (e.key === "]") {
        setRightCollapsed((prev) => !prev);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Listen for context sync errors
  useEffect(() => {
    const handleSyncError = (e: CustomEvent<{ message: string }>) => {
      message.error({
        content: e.detail.message,
        duration: 5,
        key: "context-sync-error",
      });
    };
    window.addEventListener(
      "context-sync-error",
      handleSyncError as EventListener
    );
    return () =>
      window.removeEventListener(
        "context-sync-error",
        handleSyncError as EventListener
      );
  }, []);

  // Listen for expand assistant panel requests
  useEffect(() => {
    const handleExpandAssistant = () => {
      setLeftCollapsed(false);
    };
    window.addEventListener("expand-assistant-panel", handleExpandAssistant);
    return () =>
      window.removeEventListener(
        "expand-assistant-panel",
        handleExpandAssistant
      );
  }, []);

  // Determine layout mode based on active contexts
  const activeContexts = activeContextIds
    .map((id) => contexts.get(id))
    .filter(Boolean);

  const isMultiContext = activeContexts.length > 1;

  return (
    <Layout style={{ minHeight: "100vh" }}>
      {/* Top Context Bar */}
      <Header
        style={{
          padding: "0 20px",
          background: colorBgContainer,
          borderBottom: "1px solid #e2e4ef",
          boxShadow: "0 1px 3px 0 rgba(40, 42, 100, 0.03)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          height: 56,
          lineHeight: "56px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <img
            src="/logo.png"
            alt="evo-infinito"
            style={{ height: 36, width: "auto" }}
          />
        </div>
        <div style={{ flex: 1, marginLeft: 16 }}>
          <ContextBar />
        </div>
        <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
          <Space style={{ cursor: "pointer", marginLeft: 16 }}>
            <Avatar size="small" icon={<UserOutlined />} />
            <span>{user?.name || "User"}</span>
          </Space>
        </Dropdown>
      </Header>

      <Layout>
        {/* Left Panel: AI Assistant */}
        <Sider
          width={400}
          collapsedWidth={48}
          collapsed={leftCollapsed}
          collapsible
          trigger={null}
          style={{
            background: colorBgContainer,
            borderRight: "1px solid #f1f5f9",
            boxShadow: "1px 0 3px 0 rgba(0, 0, 0, 0.02)",
            overflow: "hidden",
            transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          <div
            style={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              position: "relative",
            }}
          >
            {/* Toggle button */}
            <Tooltip
              title={leftCollapsed ? "Expand (press [)" : "Collapse (press [)"}
              placement="right"
            >
              <Button
                type="text"
                icon={
                  leftCollapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />
                }
                onClick={() => setLeftCollapsed(!leftCollapsed)}
                style={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  zIndex: 10,
                }}
              />
            </Tooltip>

            {/* Panel content */}
            <div
              style={{
                flex: 1,
                overflow: "auto",
                opacity: leftCollapsed ? 0 : 1,
                transition: "opacity 0.2s ease",
                pointerEvents: leftCollapsed ? "none" : "auto",
              }}
            >
              <AssistantPanel />
            </div>
          </div>
        </Sider>

        {/* Main Content Area */}
        <Content
          style={{
            padding: 20,
            background: "#f8f9fa",
            overflow: "auto",
            transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
            display: "flex",
            flexDirection: "column",
            alignItems: "stretch",
          }}
        >
          <RelatedPanelContext.Provider
            value={{
              isRelatedPanelOpen,
              setRelatedPanelOpen,
              relatedPanelContent,
              setRelatedPanelContent,
            }}
          >
            {children}
          </RelatedPanelContext.Provider>
        </Content>

        {/* Related Panel: Related Records (only visible when open) */}
        {isRelatedPanelOpen && (
          <Sider
            width={350}
            collapsedWidth={0}
            collapsed={false}
            collapsible
            trigger={null}
            style={{
              background: colorBgContainer,
              borderLeft: "1px solid #f1f5f9",
              borderRight: "1px solid #f1f5f9",
              boxShadow: "0 0 6px 0 rgba(0, 0, 0, 0.03)",
              overflow: "hidden",
              transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
            }}
          >
            <div
              style={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                position: "relative",
              }}
            >
              {/* Close button */}
              <Tooltip title="Close Related Panel" placement="left">
                <Button
                  type="text"
                  icon={<RightOutlined />}
                  onClick={() => setRelatedPanelOpen(false)}
                  style={{
                    position: "absolute",
                    top: 8,
                    right: 8,
                    zIndex: 10,
                  }}
                />
              </Tooltip>

              {/* Related panel content (set by Show views) */}
              <div
                style={{
                  flex: 1,
                  overflow: "auto",
                  padding: "8px 0",
                }}
              >
                {relatedPanelContent}
              </div>
            </div>
          </Sider>
        )}

        {/* Task Panel: GTD-style task board */}
        <div style={{ display: "flex", flexShrink: 0 }}>
          <TaskPanel />
        </div>

        {/* Right Panel: Context(s) */}
        <Sider
          width={isRelatedPanelOpen ? 200 : isMultiContext ? 500 : 350}
          collapsedWidth={48}
          collapsed={rightCollapsed}
          collapsible
          trigger={null}
          style={{
            background: colorBgContainer,
            borderLeft: "1px solid #f1f5f9",
            boxShadow: "-1px 0 3px 0 rgba(0, 0, 0, 0.02)",
            overflow: "hidden",
            transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          <div
            style={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              position: "relative",
            }}
          >
            {/* Toggle button */}
            <Tooltip
              title={rightCollapsed ? "Expand (press ])" : "Collapse (press ])"}
              placement="left"
            >
              <Button
                type="text"
                icon={rightCollapsed ? <LeftOutlined /> : <RightOutlined />}
                onClick={() => setRightCollapsed(!rightCollapsed)}
                style={{
                  position: "absolute",
                  top: 8,
                  left: 8,
                  zIndex: 10,
                }}
              />
            </Tooltip>

            {/* Panel content */}
            <div
              style={{
                flex: 1,
                overflow: "auto",
                opacity: rightCollapsed ? 0 : 1,
                transition: "opacity 0.2s ease",
                pointerEvents: rightCollapsed ? "none" : "auto",
              }}
            >
              {isMultiContext ? (
                // Multi-context: Split view
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                  }}
                >
                  {activeContexts.map((context, idx) => (
                    <div
                      key={context!.id}
                      style={{
                        flex: 1,
                        borderBottom:
                          idx < activeContexts.length - 1
                            ? "1px solid #f0f0f0"
                            : "none",
                        overflow: "auto",
                        position: "relative",
                      }}
                    >
                      <div
                        style={{
                          position: "sticky",
                          top: 0,
                          background: context!.color,
                          color: "#fff",
                          padding: "6px 12px",
                          fontSize: 11,
                          fontWeight: 600,
                          letterSpacing: "0.3px",
                          textTransform: "uppercase",
                          zIndex: 1,
                        }}
                      >
                        {context!.label}
                      </div>
                      <ContextPanel contextId={context!.id} />
                    </div>
                  ))}
                </div>
              ) : (
                // Single context
                <ContextPanel />
              )}
            </div>
          </div>
        </Sider>
      </Layout>

      {/* Command Palette (global) */}
      <CommandPalette />
    </Layout>
  );
};
